// src/components/reports/AchievementCharts.tsx
import { useEffect } from "react";
import { useReportsStore } from "@/store/reportsStore";
import { useAuthStore } from "@/store/authStore";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const AchievementCharts = () => {
  const { actions, deleteAction } = useReportsStore();
  const { user } = useAuthStore();

  // Для отладки
  useEffect(() => {
    console.log("[AchievementCharts] Акций в сторе:", actions.length);
    console.log("[AchievementCharts] Список акций:", actions);
  }, [actions]);

  return (
    <div className="mt-12 md:mt-16">
      <h2 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-300 to-cyan-300 mb-12 text-center md:text-left tracking-tight">
        Отчёты по акциям
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
        {actions.length === 0 ? (
          <div className="col-span-full text-center py-16 text-purple-400 text-xl">
            Пока нет акций. Добавьте первую через кнопку «+ Добавить новую акцию»
          </div>
        ) : (
          actions.map((action) => (
            <div
              key={action.id}
              className="bg-gradient-to-br from-purple-950/80 to-indigo-950/80 p-5 rounded-2xl border border-purple-600/40 shadow-lg shadow-purple-900/30 flex flex-col"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-purple-200 line-clamp-2 flex-1">
                  {action.name}
                </h3>

                {user?.id === "4" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-400 hover:text-red-300 hover:bg-red-950/40"
                    onClick={() => {
                      if (window.confirm(`Удалить акцию "${action.name}"?`)) {
                        deleteAction(action.id);
                      }
                    }}
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                )}
              </div>

              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={action.years.map((y) => ({
                    year: y.year,
                    beneficiaries: y.beneficiaries,
                    volunteers: y.volunteers,
                    ro: y.ro ?? 0,
                    layingPoints: y.layingPoints ?? 0,
                  }))}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <XAxis dataKey="year" stroke="#a78bfa" />
                  <YAxis stroke="#a78bfa" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e1b4b",
                      border: "1px solid #7c3aed",
                      borderRadius: "8px",
                      color: "white",
                    }}
                  />
                  <Legend wrapperStyle={{ color: "#d8b4fe" }} />

                  {/* Благополучатели */}
                  <Line
                    type="monotone"
                    dataKey="beneficiaries"
                    stroke="#c084fc"
                    strokeWidth={3}
                    dot={{ r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Благополучатели"
                  />

                  {/* Добровольцы */}
                  <Line
                    type="monotone"
                    dataKey="volunteers"
                    stroke="#22d3ee"
                    strokeWidth={3}
                    dot={{ r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Добровольцы"
                  />

                  {/* РО — показываем только если есть значения > 0 */}
                  {action.years.some((y) => (y.ro ?? 0) > 0) && (
                    <Line
                      type="monotone"
                      dataKey="ro"
                      stroke="#ec4899"        // розовый в стиле приложения
                      strokeWidth={3}
                      dot={{ r: 5 }}
                      activeDot={{ r: 8 }}
                      name="РО"
                    />
                  )}

                  {/* Точки возложения — показываем только если есть значения > 0 */}
                  {action.years.some((y) => (y.layingPoints ?? 0) > 0) && (
                    <Line
                      type="monotone"
                      dataKey="layingPoints"
                      stroke="#f59e0b"        // оранжевый в стиле приложения
                      strokeWidth={3}
                      dot={{ r: 5 }}
                      activeDot={{ r: 8 }}
                      name="Точки возложения"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>

              <div className="mt-4 text-sm text-purple-300">
                Годов: <span className="font-bold text-white">{action.years.length}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AchievementCharts;